

import { 
    GEMINI_OCR_MODEL, 
    NUMBER_OF_MARKING_RUNS, 
    DEFAULT_SYSTEM_INSTRUCTIONS,
    GEMINI_SCORING_TEMPERATURE,
    GEMINI_FEEDBACK_TEMPERATURE,
    QUOTA_EXCEEDED_USER_MESSAGE,
    GEMINI_META_ANALYSIS_TEMPERATURE,
    MODELS
} from '../constants';
import { extractBandOrder, parseBandRange } from './assessmentService';
import { MarkingRunData, RubricTransformContext, RubricTransformResponse } from '../types';
import { addAdminNotification } from './adminNotifications';

const API_INITIALIZATION_ERROR = 'GEMINI_PROXY_UNAVAILABLE::The Gemini proxy server is not available. Please ensure it is running and you are authenticated.';


// Get API base URL based on environment
const getApiBaseUrl = (): string => {
  // Check if we're in production
  if (import.meta.env.PROD) {
    // Use production API URL - you'll need to set this in your environment variables
    return import.meta.env.VITE_API_BASE_URL || 'https://us-central1-history-essay-bot.cloudfunctions.net/api';
  }
  // Development/local environment
  return 'http://localhost:3001';
};

// SECURITY: Gemini API is now accessed through secure backend proxy
// API calls require Firebase authentication
const getGeminiProxyUrl = (): string => `${getApiBaseUrl()}/api/gemini`;

// These safety settings are locked to `BLOCK_NONE` to ensure that analysis of historical
// documents, which may contain sensitive topics, is not interrupted by content filters.
// This is appropriate for a closed, educational context.
const SAFETY_SETTINGS = [
    { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE' },
    { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE' },
    { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE' },
    { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE' },
];

const extractTextFromGeminiResponse = (data: any): string => {
    const parts = data?.candidates?.[0]?.content?.parts;
    if (Array.isArray(parts)) {
        // Thought parts (Gemini 3 thinking) can also carry text; joining them into
        // a JSON response corrupts it, so they must be excluded.
        const text = parts
            .filter((part: any) => !part?.thought)
            .map((part: any) => part?.text)
            .filter(Boolean)
            .join('\n')
            .trim();
        if (text) return text;
    }
    throw new Error('No text returned from Gemini response');
};

const getAuthToken = async () => {
    const { auth } = await import('./firebase');
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User must be authenticated to use AI services');
    }
    return user.getIdToken();
};

const callGeminiProxy = async (requestBody: any) => {
    try {
        const token = await getAuthToken();
        const response = await fetch(getGeminiProxyUrl(), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            const err: any = new Error(`Gemini proxy error: ${response.status} ${response.statusText}: ${errorText}`);
            err.httpStatus = response.status;
            throw err;
        }

        return response.json();
    } catch (error: any) {
        if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
            const proxyError: any = new Error(API_INITIALIZATION_ERROR);
            proxyError.cause = error;
            throw proxyError;
        }
        throw error;
    }
};

const generateTextFromGemini = async ({
    modelId,
    parts,
    generationConfig,
    thinking,
    safetySettings = SAFETY_SETTINGS
}: {
    modelId: string,
    parts: any[],
    generationConfig?: any,
    thinking?: { level?: 'low' | 'high' },
    safetySettings?: any[]
}) => {
    const data = await callGeminiProxy({
        model: modelId,
        contents: [{ parts }],
        generationConfig,
        thinking,
        safetySettings
    });
    // Log token usage if available
    const usage = data?.usageMetadata || {};
    const usageLog = {
        promptTokenCount: usage.promptTokenCount ?? null,
        candidatesTokenCount: usage.candidatesTokenCount ?? null,
        totalTokenCount: usage.totalTokenCount ?? null,
        thoughtsTokenCount: usage.thoughtsTokenCount ?? null,
        promptTokensDetails: usage.promptTokensDetails ?? null,
        candidatesTokensDetails: usage.candidatesTokensDetails ?? null,
    };
    console.info('[GeminiDebug] tokenUsage:', usageLog);
    if (typeof window !== 'undefined') {
        (window as any).__lastGeminiTokenUsage = usageLog;
    }
    const totalTokens = usage?.totalTokenCount;
    if (typeof totalTokens === 'number' && totalTokens > 30000) {
        addAdminNotification('token-threshold', 'Gemini token usage exceeded 30k', {
            totalTokenCount: totalTokens,
            promptTokenCount: usage?.promptTokenCount ?? null,
            candidatesTokenCount: usage?.candidatesTokenCount ?? null,
            responseId: data?.responseId ?? null,
            model: modelId
        });
    }
    try {
        const text = extractTextFromGeminiResponse(data);
        return { text, raw: data };
    } catch (err) {
        throw err;
    }
};

/**
 * A centralized error handler for all Gemini API calls.
 * It checks for specific, common errors and provides user-friendly messages.
 * @param {any} error - The error object caught from the API call.
 * @param {string} context - A string describing where the error occurred (e.g., 'image transcription').
 * @param {string} [rawResponseText=''] - Optional raw text from the AI if a parsing error occurs.
 * @throws {Error} Throws a new, more informative error.
 */
function handleGeminiError(error: any, context: string, rawResponseText = '') {
    const errorMessage = error instanceof Error ? error.message : String(error);

    // This check is now for when the server-side Gemini key is missing or invalid.
    if (errorMessage.includes("GEMINI_API_KEY_INVALID") || errorMessage.includes("API_KEY_INVALID") || errorMessage.includes("API key not valid")) {
        console.error(`Critical Gemini Error during ${context}: The server-side Gemini API key is invalid or missing.`, error);
        throw new Error("API_KEY_INVALID::The Gemini API key configured on the server is invalid or missing. Please update the server's GEMINI_API_KEY.");
    }

    // Proxy not reachable or misconfigured
    if (errorMessage.includes("GEMINI_PROXY_UNAVAILABLE")) {
        throw new Error(API_INITIALIZATION_ERROR);
    }

    // Pass through user-friendly quota message from the retry logic
    if (error instanceof Error && error.message === QUOTA_EXCEEDED_USER_MESSAGE) {
        throw error; 
    }

    // Handle JSON parsing errors with more context
    if (error instanceof SyntaxError && error.message.toLowerCase().includes("json")) {
        console.error(`JSON Parsing Error during ${context}. Raw AI response was:`, rawResponseText);
        throw new Error(`Failed to parse AI response as JSON during ${context}. Check the console for the raw AI output.`);
    }

    // Generic fallback for other errors
    console.error(`Error during ${context} (after retries if applicable):`, error);
    throw new Error(`An error occurred during ${context}. Details: ${errorMessage}`);
}


const MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 1000;

function isQuotaError(error: any) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  // Check for gRPC status code for RESOURCE_EXHAUSTED or HTTP 429
  if (error && (error.code === 8 || error.httpStatus === 429 || (error.httpError && error.httpError.status === 429))) {
    return true;
  }
  return errorMessage.includes("RESOURCE_EXHAUSTED") || 
         errorMessage.includes("quota") ||
         errorMessage.includes("429") ||
         errorMessage.toLowerCase().includes("rate limit");
}

function isJsonParsingError(error: any) {
  if (error instanceof SyntaxError) {
    return error.message.toLowerCase().includes("json");
  }
  
  const errorMessage = error instanceof Error ? error.message : String(error);
  return errorMessage.toLowerCase().includes("failed to parse") ||
         errorMessage.toLowerCase().includes("json") ||
         errorMessage.toLowerCase().includes("syntax");
}


async function callGeminiWithRetry(
    apiCallFn: () => Promise<any>,
    maxRetries = MAX_RETRIES,
    initialDelayMs = INITIAL_DELAY_MS
) {
    let attempts = 0;
    let currentDelayMs = initialDelayMs;

    while (attempts < maxRetries) {
        try {
            return await apiCallFn();
        } catch (error) {
            attempts++;
            const isQuotaErr = isQuotaError(error);
            console.warn(`Gemini API call attempt ${attempts} failed. Is Quota Error: ${isQuotaErr}. Error: ${error.message}`);

            if (isQuotaErr) {
                if (attempts >= maxRetries) {
                    console.error(`Max retries (${maxRetries}) reached for quota error. Throwing user-friendly message.`);
                    throw new Error(QUOTA_EXCEEDED_USER_MESSAGE);
                }
                console.log(`Retrying after ${currentDelayMs}ms delay due to quota error...`);
                await new Promise(resolve => setTimeout(resolve, currentDelayMs));
                currentDelayMs *= 2; 
            } else {
                throw error; 
            }
        }
    }
    throw new Error("Gemini API call failed after multiple retries."); 
}

export const analyzePastedImage = async (imageBase64: string) => {
  if (!imageBase64) throw new Error("No image data provided for analysis.");

  const imagePart = {
    inlineData: {
      mimeType: 'image/jpeg',
      data: imageBase64,
    },
  };

  const textPart = {
    text: "Analyze this image. Your primary goal is to extract information useful for an essay. " +
          "1. First, determine if the image is primarily textual (like a scanned document, a screenshot of text) or pictorial (like a photograph, chart, or drawing). " +
          "2. If it is primarily textual, transcribe all the text accurately. Preserve paragraph structure. If no text is discernible, state that clearly. " +
          "3. If it is primarily pictorial, provide a detailed description of the image's content. Focus on elements that would be relevant to a historical or analytical essay (e.g., describe the people, actions, setting, symbols in a photograph; explain the data and trends in a chart). " +
          "4. If it's a mix, describe the visual elements and transcribe any significant text. " +
          "Present your analysis clearly."
  };

  try {
    const { text } = await callGeminiWithRetry(() => 
        generateTextFromGemini({
            modelId: GEMINI_OCR_MODEL,
            parts: [imagePart, textPart],
            safetySettings: SAFETY_SETTINGS
        })
    );
    return text;
  } catch (error) {
    handleGeminiError(error, 'pasted image analysis');
    }
};

/**
 * Uses Gemini LLM to calculate word count for essay text
 * This provides a more sophisticated word counting method that can handle
 * complex formatting, OCR artifacts, and other text processing challenges
 */
export const calculateWordCountWithGemini = async (essayText: string, modelConfig: any = null) => {
    if (!essayText) return 0;

    // Use a default model config if none provided
    const config = modelConfig || MODELS['gemini-2.5-flash'];

    const prompt = `
        You are a precise text analysis assistant. Your task is to count the total number of words in the provided essay text.

        **WORD COUNTING RULES:**
        1. Count every meaningful word in the text
        2. Ignore page markers like "--- Page 1 ---" or similar formatting artifacts
        3. Count hyphenated words as single words (e.g., "well-known" = 1 word)
        4. Count contractions as single words (e.g., "don't" = 1 word)  
        5. Count numbers written as words or digits as single words (e.g., "twenty-one" = 1 word, "1945" = 1 word)
        6. Ignore any transcription artifacts or OCR errors but count the intended words
        7. Do not count title text, headers, or page numbers unless they are part of the essay content

        **ESSAY TEXT TO ANALYZE:**
        \`\`\`
        ${essayText}
        \`\`\`

        **RESPONSE FORMAT:**
        Respond with a JSON object containing only the word count as a number:
        \`\`\`json
        {
          "wordCount": [number]
        }
        \`\`\`
    `;

    let rawResponse = '';
    try {
        const { text } = await callGeminiWithRetry(() =>
            generateTextFromGemini({
                modelId: config.apiModel,
                parts: [{ text: prompt }],
                generationConfig: {
                    responseMimeType: "application/json",
                    temperature: 0.1, // Very low temperature for consistent counting
                    ...config.config
                },
                safetySettings: SAFETY_SETTINGS
            })
        );
        rawResponse = text;
        const parsedResult = robustJsonParser(rawResponse);
        
        // Validate the result
        if (typeof parsedResult.wordCount !== 'number' || parsedResult.wordCount < 0) {
            throw new Error('Invalid word count returned from Gemini');
        }
        
        return parsedResult.wordCount;
    } catch (error) {
        // Fallback to local calculation if Gemini fails
        console.warn(`Gemini word count failed, falling back to local calculation:`, error);
        const { calculateWordCount } = await import('./assessmentService');
        return calculateWordCount(essayText);
    }
};


export const transcribeImage = async (imageBase64: string) => {
  if (!imageBase64) throw new Error("No image data provided for transcription.");

  const imagePart = {
    inlineData: {
      mimeType: 'image/jpeg', 
      data: imageBase64,
    },
  };
  const textPart = {
    text: "Transcribe all handwritten and printed text in this image accurately. Preserve paragraph structure if possible. If the image contains no discernible text, respond with 'NO_TEXT_DETECTED'.",
  };

  try {
    const { text } = await callGeminiWithRetry(() => 
        generateTextFromGemini({
            modelId: GEMINI_OCR_MODEL,
            parts: [imagePart, textPart],
            safetySettings: SAFETY_SETTINGS
        })
    );
    if (text.trim().toUpperCase() === 'NO_TEXT_DETECTED') {
        return "No text was detected in this image page.";
    }
    return text;
  } catch (error) {
    handleGeminiError(error, 'image transcription');
  }
};

/**
 * A highly resilient function to find and parse a JSON object from a string.
 * It handles markdown fences, conversational text, and other AI response variations.
 * @param {string} text - The raw text response from the AI model.
 * @returns {object} The parsed JSON object.
 * @throws {SyntaxError} If a valid JSON object cannot be found or parsed.
 */
/**
 * Scans for the first complete, balanced JSON value ('{...}' or '[...]') in a string,
 * ignoring brackets inside string literals. Returns null if none is found.
 * This protects against trailing garbage after the JSON (e.g. a stray '}').
 */
function extractFirstJsonValue(str: string): string | null {
    const start = str.search(/[{[]/);
    if (start === -1) return null;
    const open = str[start];
    const close = open === '{' ? '}' : ']';
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < str.length; i++) {
        const ch = str[i];
        if (inString) {
            if (escaped) {
                escaped = false;
            } else if (ch === '\\') {
                escaped = true;
            } else if (ch === '"') {
                inString = false;
            }
        } else if (ch === '"') {
            inString = true;
        } else if (ch === open) {
            depth++;
        } else if (ch === close) {
            depth--;
            if (depth === 0) {
                return str.substring(start, i + 1);
            }
        }
    }
    return null;
}

function robustJsonParser(text: any) {
    if (typeof text !== 'string') {
        throw new SyntaxError("Input to parser must be a string.");
    }
    let jsonStr = text.trim();

    // Strategy 1: Find a JSON block within markdown fences (e.g., ```json ... ```)
    const fenceMatch = jsonStr.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (fenceMatch && fenceMatch[1]) {
        jsonStr = fenceMatch[1].trim();
    } else {
        // Strategy 2: If no fences, find the main JSON object or array.
        // It looks for the content between the first '{' or '[' and the last '}' or ']'.
        const firstBracket = jsonStr.indexOf('{');
        const firstSquare = jsonStr.indexOf('[');
        let firstIndex = -1;

        if (firstBracket === -1) {
            firstIndex = firstSquare;
        } else if (firstSquare === -1) {
            firstIndex = firstBracket;
        } else {
            firstIndex = Math.min(firstBracket, firstSquare);
        }

        if (firstIndex !== -1) {
            const lastCurly = jsonStr.lastIndexOf('}');
            const lastSquare = jsonStr.lastIndexOf(']');
            const lastIndex = Math.max(lastCurly, lastSquare);

            if (lastIndex > firstIndex) {
                jsonStr = jsonStr.substring(firstIndex, lastIndex + 1);
            }
        }
    }

    // Strategy 3: Extract the first complete, balanced JSON value. This handles
    // responses where valid JSON is followed by trailing garbage (e.g. a stray '}'),
    // which the first-to-last-bracket substring above would wrongly include.
    const balanced = extractFirstJsonValue(jsonStr);
    if (balanced) {
        try {
            return JSON.parse(balanced);
        } catch {
            // Fall through to the cleaning heuristics below.
        }
    }

    // Now that we have a candidate string, clean and parse it.

    let cleanedJsonStr = jsonStr;

    // Fix missing comma between correctly closed objects, even across newlines.
    cleanedJsonStr = cleanedJsonStr.replace(/\}\s*(?=[\r\n\s]*\{)/g, '}, {');
    // Fix missing comma between arrays/objects and next quoted key.
    cleanedJsonStr = cleanedJsonStr.replace(/}\s*(?=\s*["'])/g, '},');
    cleanedJsonStr = cleanedJsonStr.replace(/\]\s*(?=\s*["'])/g, '],');
    
    // Remove trailing commas before a closing bracket or brace.
    cleanedJsonStr = cleanedJsonStr.replace(/,(?=\s*?[}\]])/g, '');

    try {
        return JSON.parse(cleanedJsonStr);
    } catch (e) {
        console.error("Final attempt to parse cleaned JSON failed. Original text:", text);
        console.error("Cleaned string was:", cleanedJsonStr);
        throw new SyntaxError(`Failed to parse JSON even after cleaning. Reason: ${e.message}`);
    }
}


/**
 * A flexible check to see if a string contains exactly two sentences.
 * It splits by common terminal punctuation and filters out empty results.
 * @param {string} text The string to check.
 * @returns {boolean} True if the string contains exactly two sentences.
 */
const isValidTwoSentences = (text: any) => {
    if (typeof text !== 'string' || !text) return false;
    // Split by common terminal punctuation. Filter out empty strings that result from trailing punctuation or multiple separators.
    const sentences = text.trim().split(/[.?!]/).filter(s => s.trim().length > 0);
    return sentences.length === 2;
};


export const markEssay = async (
  transcribedText: string,
  systemInstructions: string,
  markingRubric: string, 
  assessmentQuestion: string,
  yearLevel: string,
  modelConfig: any,
  exemplars: any[] = [],
  essayInputType: string,
  assessmentType: string | null = null,
  examCondition: string | null = null,
  minWordCount: number | null = null,
  maxWordCount: number | null = null,
  onStatusUpdate?: (message: string) => void,
  retryAttempt: number = 0
) => {
  let exemplarBlock = '';
  if (exemplars && exemplars.length > 0) {
    exemplarBlock += "\n\n### MARKED EXEMPLARS FOR REFERENCE\n";
    exemplarBlock += "Use these as a guide for calibrating your marking against the rubric.\n\n";
    exemplars.forEach((ex: any, i: number) => {
      exemplarBlock += `--- Exemplar ${i + 1} ---\n`;
      exemplarBlock += `**Year Level:** ${ex.yearLevel || 'Not specified'}\n`;
      exemplarBlock += `**Essay Text:**\n${ex.essayText}\n\n`;
      exemplarBlock += `**Marks Given:**\n`;
      ex.criteriaBands.forEach((cb: any) => {
        exemplarBlock += `- ${cb.name}: ${cb.band}\n`;
      });
      exemplarBlock += `**Overall Score:** ${ex.overallScore}\n`;
      if (ex.explanation) {
        exemplarBlock += `**Explanation:** ${ex.explanation}\n`;
      }
      exemplarBlock += `--- End Exemplar ${i + 1} ---\n\n`;
    });
  }
  
  let transcriptionCaveat = '';
  if (essayInputType === 'images') {
    transcriptionCaveat = `
    
    **Special Transcription Consideration:** As this essay was transcribed from images, you MUST be forgiving of awkward sentences, grammatical errors, or unfinished phrases. These are likely transcription artifacts. Evaluate the underlying ideas, not the literal text quality.`;
  }

  // Assessment context
  let assessmentContext = '';
  if (assessmentType) {
    assessmentContext += `\n    - Assessment Type: ${assessmentType}`;
  }
  if (examCondition) {
    assessmentContext += `\n    - Exam Condition: ${examCondition}`;
  }
  if (minWordCount || maxWordCount) {
    assessmentContext += `\n    - Expected Word Count: ${minWordCount || 'No minimum'} - ${maxWordCount || 'No maximum'} words`;
  }

  const prompt = `
    Role: You are an Australian Highschool History teacher. You are fair in your marking approach and consider a students age and year level in the marking your conduct so that grades are applied appropriate to the expectations of students at their levels.

    **ASSESSMENT DETAILS:**
    - Year Level: ${yearLevel || 'Not specified'}
    - Essay Question: ${assessmentQuestion}${assessmentContext}

    **MARKING RUBRIC:**
    ${markingRubric}
    ${exemplarBlock}

    **ESSAY TO MARK:**
    \`\`\`
    ${transcribedText}
    \`\`\`

    Task:
    Step 1 
    You should start by planning out the marking process you will take on this essay. Review the marking criterion, review the type of assessment response by looking at the attributes of the assessment and expected word count range. Consider yourself a competent and fair high school Australian history teacher and reason through how you would effectively and fairly grade this response.

    Step 2
    Start by reading the whole response and get a general sense of the quality of the response. Consider how it's length by word count compared to the expected range.
    Consider the depth of the response and how well structured, coherent, accurate and (most importantly) relevant the response is to the question. You don't consider or penalise for spelling errors or small grammatical errors. Form an overall impression of the quality of the response and level of understanding the student is clearly demonstrating in terms of their understanding of the question, understanding of the topic, their historical knowledge and competence as a student. Use this general impression to guide how you determine which band descriptors best apply for each marking criterion and which mark you will award for each band.${transcriptionCaveat}

        Step 3
        Review the first marking criterion and carefully review each band descriptor one by one, and evaluate how much that band descriptor describes the response. Create a match score out of 100 and a confidence score out of 100 for how much the descriptor describes the response and your confidence level in your matching score. Select the band descriptor that has the highest match score and write as a 2 sentence justification as to why that band descriptor is the best fit and match to the response.
        
        Additionally, for the selected band, provide a 3 sentence justification explaining why you DIDN'T select the band descriptor immediately above (if one exists) and why you DIDN'T select the band descriptor immediately below (if one exists). This will help teachers understand why the response didn't meet the higher standard or why it exceeded the lower standard.

    Step 4
    Assign a score for your selected band that is between the range for that band descriptor. Use your matching scores and confidence scores across all the band descriptors to determine if you award on the higher end of the range for that band or the lower end. For example, if it fully met one band, but also partially met the band above, then consider a higher score on the range for the selected band. 
    Go through the rest of the remaining marking criterion following this process.
    Note that I don't want you to hold back in awarding top marks when a student response is out standing. I also want to make sure you don't shy away from giving responses the lowest band when marking a response when it's lacking serious depth of understanding and has very limited sophistication.

    Step 5
    Work out an overall mark for the essay by adding together all of the scores for each criterion according to the respective weighting of each criterion. The weighting is what determines how much of that criterion's score contributes to the overall mark.

    Additionally, provide comprehensive feedback for the student that includes:
    - What they did well (with specific examples from their essay)
    - Areas for improvement with detailed, actionable advice on how to enhance their analysis, evidence use, and argument structure
    - Specific suggestions that would elevate the quality of their historical writing

        **RESPONSE FORMAT:**
        You MUST respond with a single, valid JSON object. Do not include any text or markdown formatting outside of the JSON block. The structure must be:
        \`\`\`json
        {
          "markingPlan": "Step 1: Your planning explanation for how you will approach marking this assessment",
          "generalImpression": "Step 2: Your overall assessment of the essay quality, structure, and student understanding",
          "criteriaResults": [
            {
              "criterionName": "Name of Criterion 1",
              "bandAnalysis": [
                {"bandName": "21-25", "matchScore": 85, "confidenceScore": 90},
                {"bandName": "16-20", "matchScore": 40, "confidenceScore": 75}
              ],
              "selectedBand": "21-25",
              "justification": "Two sentence explanation of why this band descriptor is the best fit for this response.",
              "justificationBandAbove": "Three sentence explanation of why the band above was NOT selected (null if no band above exists).",
              "justificationBandBelow": "Three sentence explanation of why the band below was NOT selected (null if no band below exists).",
              "score": 23
            }
          ],
          "overallEssayScore": 18,
          "detailedFeedback": {
            "strengths": "What the student did well, with specific examples from their essay",
            "improvementAreas": "Detailed actionable advice on how to elevate the essay quality, focusing on sophisticated analysis, comprehensive evidence use, and enhanced structure", 
            "specificSuggestions": "Concrete examples of how they could have enhanced specific parts of their essay"
          }
        }
        \`\`\`
  `;

  let rawResponse = '';
  try {
    if (onStatusUpdate) {
      const attemptText = retryAttempt > 0 ? ` (Retry ${retryAttempt}/3)` : '';
      onStatusUpdate(`Analyzing essay with AI${attemptText}...`);
    }

    const { text } = await callGeminiWithRetry(() =>
      generateTextFromGemini({
        modelId: modelConfig.apiModel,
        parts: [{ text: prompt }],
        generationConfig: {
          responseMimeType: "application/json",
          temperature: GEMINI_SCORING_TEMPERATURE,
          maxOutputTokens: 30000,
          ...modelConfig.config
        },
        // Gemini 3 thinking support. Only meaningful for gemini-3-* models.
        thinking: { level: 'high' },
        safetySettings: SAFETY_SETTINGS
      })
    );
    rawResponse = text;
    const parsedResult = robustJsonParser(rawResponse);

    // A response can be valid JSON yet unusable for scoring (missing criteria or
    // non-numeric scores would propagate NaN into the final overall score).
    // Throwing a JSON-flavoured SyntaxError routes it into the existing retry logic.
    const criteria = parsedResult?.criteriaResults;
    const hasValidScores = Array.isArray(criteria) && criteria.length > 0 &&
        criteria.every((c: any) => c && Number.isFinite(Number(c.score)));
    if (!hasValidScores) {
        console.error("Parsed marking JSON is missing criteriaResults with numeric scores:", parsedResult);
        throw new SyntaxError("Parsed AI marking JSON is invalid: criteriaResults must be a non-empty array with numeric scores.");
    }

    return { ...parsedResult, prompt };
  } catch (error) {
    // Check if this is a JSON parsing error and we haven't exhausted retries
    if (isJsonParsingError(error) && retryAttempt < 3) {
      console.warn(`JSON parsing failed on attempt ${retryAttempt + 1}, retrying...`, error);
      if (onStatusUpdate) {
        onStatusUpdate(`JSON parsing failed, retrying (${retryAttempt + 1}/3)...`);
      }
      // Wait a short time before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Retry with incremented attempt counter
      return markEssay(
        transcribedText,
        systemInstructions,
        markingRubric,
        assessmentQuestion,
        yearLevel,
        modelConfig,
        exemplars,
        essayInputType,
        assessmentType,
        examCondition,
        minWordCount,
        maxWordCount,
        onStatusUpdate,
        retryAttempt + 1
      );
    }
    
    handleGeminiError(error, 'essay marking', rawResponse);
  }
};


export const runMetaAnalysis = async (
  essayText: string,
  rubric: string,
  assessmentQuestion: string,
  yearLevel: string,
  conflictingRunsData: MarkingRunData[]
) => {
  let summaryOfRuns = conflictingRunsData.map(run => {
    const criteriaSummary = (run.criteriaResults || [])
      .map(c => `${c.criterionName}: ${c.score}`)
      .join(', ');
    return `Run ${run.runNumber}: Overall Score ${run.overallEssayScore?.toFixed(1) || 'N/A'} (Criteria: ${criteriaSummary})`;
  }).join('\n');

  const prompt = `
    You are an expert history marking moderator. You have been given several AI-generated reviews for a single essay, and they show significant disagreement. Your task is to act as an impartial and decisive final marker.

    **ASSESSMENT DETAILS:**
    - Year Level: ${yearLevel}
    - Essay Question: ${assessmentQuestion}

    **FULL MARKING RUBRIC:**
    ${rubric}

    **ESSAY TEXT:**
    \`\`\`
    ${essayText}
    \`\`\`

    **CONFLICTING REVIEWS:**
    Here is a summary of the conflicting scores from the initial AI markers. Note the variance.
    ${summaryOfRuns}

    **YOUR TASK:**
    Review the essay and the rubric one last time with extreme care. Your judgment will be the deciding one. Provide a single, definitive, and final assessment for this essay by assigning a score and band for each criterion.

    **RESPONSE FORMAT:**
    You MUST respond with a single, valid JSON object. Do not include any text or markdown formatting outside of the JSON block. The structure must be identical to the original markers' format:
    \`\`\`json
    {
      "criteriaResults": [
        {
          "criterionName": "Name of Criterion 1",
          "selectedBand": "e.g., 6-10",
          "score": 8
        }
      ],
      "overallEssayScore": 11,
      "overallEssayBand": "e.g., 11-15",
      "overallConfidenceForRun": "High"
    }
    \`\`\`
  `;
  
  const metaModelConfig = MODELS['gemini-3-pro-preview'];
  let rawResponse = '';
  try {
    const { text } = await callGeminiWithRetry(() =>
      generateTextFromGemini({
        modelId: metaModelConfig.apiModel,
        parts: [{ text: prompt }],
        generationConfig: {
          responseMimeType: "application/json",
          temperature: GEMINI_META_ANALYSIS_TEMPERATURE,
          ...metaModelConfig.config
        },
        safetySettings: SAFETY_SETTINGS
      })
    );
    rawResponse = text;
    const parsedResult = robustJsonParser(rawResponse);
    // Return a full run object for seamless integration. The runNumber 'Meta' helps in debugging.
    return { ...parsedResult, prompt, runNumber: 'Meta' };
  } catch (error) {
    handleGeminiError(error, 'meta-analysis moderation', rawResponse);
  }
};


export const generateConsolidatedJustifications = async (
  transcribedText: string, 
  markingRubric: string, 
  assessmentQuestion: string, 
  consolidatedCriteria: any, 
  allMarkingRunsData: any, 
  yearLevel: string, 
  modelConfig: any
) => {
  const justificationsPrompt = `
    You are an expert history teaching assistant. You have been provided with the results of multiple AI marking runs for a student's essay. Your task is to synthesize these results into a single, coherent, and well-justified assessment for each criterion.

    **BACKGROUND:**
    - Year Level: ${yearLevel}
    - Essay Question: ${assessmentQuestion}

    **FULL MARKING RUBRIC:**
    ${markingRubric}

    **STUDENT'S ESSAY TEXT:**
    \`\`\`
    ${transcribedText}
    \`\`\`

    **CONSOLIDATED MARKING RESULTS (AVERAGES):**
    This is the final, averaged assessment you need to justify:
    \`\`\`json
    ${JSON.stringify(consolidatedCriteria, null, 2)}
    \`\`\`

    **RAW DATA FROM INDIVIDUAL AI MARKING RUNS (FOR CONTEXT):**
    Here are the raw thoughts from each AI marker. Use this to understand the variance and common themes.
    \`\`\`json
    ${JSON.stringify(allMarkingRunsData, null, 2)}
    \`\`\`

    **YOUR TASK:**
    For each criterion in the "CONSOLIDATED MARKING RESULTS", write a concise, evidence-based justification for the given score and band.
    1.  Your justification must be written in a helpful, encouraging tone suitable for a student.
    2.  You MUST cite specific (but brief) quotes or references from the student's essay to support your points.
    3.  If the AI markers consistently noted a weakness (e.g., from the 'missingDescriptors' in the raw data), incorporate that into your feedback.
    4.  **Factual Accuracy Check:** While writing your justification, if you find a statement in the essay that seems factually incorrect (e.g., "Germany invaded Poland in 1940"), you MUST flag it by enclosing the incorrect part of your justification in special markers, like this: "The essay correctly identifies the trigger event, but a minor inaccuracy was noted regarding the date of the invasion of Poland (?)." The marker is \`(?)\`. Use this marker sparingly and only for clear factual errors, not differences in interpretation.

    **RESPONSE FORMAT:**
    You MUST respond with a single, valid JSON object. Do not include any text outside the JSON block. The structure must be:
    \`\`\`json
    {
      "justifications": [
        {
          "criterionName": "Name of Criterion 1",
          "justification": "The justification for criterion 1, with specific quotes from the essay..."
        },
        {
          "criterionName": "Name of Criterion 2",
          "justification": "The justification for criterion 2..."
        }
      ]
    }
    \`\`\`
  `;
  
  let rawResponse = '';
  try {
    const { text } = await callGeminiWithRetry(() =>
        generateTextFromGemini({
            modelId: modelConfig.apiModel,
            parts: [{ text: justificationsPrompt }],
            generationConfig: {
                responseMimeType: "application/json",
                temperature: GEMINI_FEEDBACK_TEMPERATURE, // Higher temp for more creative prose
                ...modelConfig.config
            },
            safetySettings: SAFETY_SETTINGS
        })
    );
    rawResponse = text;
    const parsedResult = robustJsonParser(rawResponse);
    return { ...parsedResult, prompt: justificationsPrompt };
  } catch (error) {
    handleGeminiError(error, 'justification generation', rawResponse);
  }
};


export const generateFeedback = async (
    transcribedText: string, 
    systemInstructions: string, 
    markingRubric: string, 
    assessmentQuestion: string, 
    finalConsolidatedResults: any, 
    finalOverallScore: number, 
    finalOverallBand: string, 
    allMarkingRunsData: any, 
    yearLevel: string, 
    modelConfig: any,
    consolidatedAuditData: any
) => {
    // Create a summary of what the student missed for feedback generation.
    let auditSummary = "Key areas for improvement noted by the markers include:\n";
    let hasAuditPoints = false;
    for (const criterionName in consolidatedAuditData) {
        const missed = consolidatedAuditData[criterionName].missed;
        if (missed.length > 0) {
            hasAuditPoints = true;
            auditSummary += `* For "${criterionName}": ${missed.join(', ')}\n`;
        }
    }
    if (!hasAuditPoints) {
        auditSummary = "The markers did not flag any specific missing elements from the top bands.";
    }

    const feedbackPrompt = `
        You are an expert history teacher, specializing in providing constructive, actionable feedback to students. Your tone should be encouraging, positive, and focused on helping the student improve.

        **BACKGROUND:**
        - Student's Year Level: ${yearLevel}
        - Essay Question: ${assessmentQuestion}

        **STUDENT'S FINAL ASSESSMENT:**
        This is the final mark the student received after consolidating multiple AI opinions.
        - Overall Score: ${finalOverallScore}
        - Overall Band: ${finalOverallBand}
        - Per-Criterion Breakdown:
        \`\`\`json
        ${JSON.stringify(finalConsolidatedResults, null, 2)}
        \`\`\`
        
        **INTERNAL AUDIT SUMMARY (What the student missed from top bands):**
        ${auditSummary}

        **YOUR TASK:**
        Write comprehensive feedback for the student in Markdown format. The feedback should have two main sections: "What You've Done Well" and "Areas for Improvement".

        1.  **Structure and Tone:**
            -   Start with a positive and encouraging opening statement.
            -   Use Markdown for formatting (e.g., ### for headings, * for bullet points, ** for bolding).
            -   The feedback should be about 3-4 paragraphs long in total.

        2.  **Content - What You've Done Well:**
            -   Identify at least two specific strengths of the essay.
            -   Use the "justification" field from the student's final assessment to guide you.
            -   Cite brief, specific examples from the essay text to make your feedback tangible.

        3.  **Content - Areas for Improvement:**
            -   Identify two key areas where the student can improve.
            -   Use the "INTERNAL AUDIT SUMMARY" as your primary source for these points. This tells you what the essay was lacking compared to a top-band response.
            -   Provide clear, actionable advice. Instead of saying "Your argument was weak," say "To strengthen your argument, consider exploring the counter-arguments to X, or providing more specific data to support your claim about Y."
            -   Frame these points constructively (e.g., "To take this to the next level...").

        4.  **Concluding Statement:**
            -   End with a positive and forward-looking summary.

        **EXAMPLE OF A GOOD FEEDBACK POINT:**
        > *   **Excellent Use of Primary Sources:** Your use of the source from John Doe was particularly effective. When you wrote, "**students' quote from their essay**," it powerfully supported your point about the economic impact.

        Begin writing the feedback now.
    `;
    
    try {
        const { text } = await callGeminiWithRetry(() =>
            generateTextFromGemini({
                modelId: modelConfig.apiModel,
                parts: [{ text: feedbackPrompt }],
                generationConfig: {
                    temperature: GEMINI_FEEDBACK_TEMPERATURE,
                    ...modelConfig.config
                },
                safetySettings: SAFETY_SETTINGS
            })
        );
        return text;
    } catch (error) {
        handleGeminiError(error, 'feedback generation');
    }
};

export const explainMarkingVariance = async (essayData: any, assessment: any, teacherFeedback: string, modelConfig: any, selectedModelForComparison?: string) => {
    // Extract AI marking data from the selected model or fallback to legacy fields
    let aiMarkingData;
    if (selectedModelForComparison && essayData.modelResults && essayData.modelResults[selectedModelForComparison]) {
        // Use the selected model's results
        const selectedModelResult = essayData.modelResults[selectedModelForComparison];
        aiMarkingData = {
            finalOverallScore: selectedModelResult.finalOverallScore,
            consolidatedMarkingResults: selectedModelResult.consolidatedMarkingResults,
            modelName: selectedModelResult.modelName || MODELS[selectedModelForComparison]?.name || selectedModelForComparison
        };
    } else {
        // Fallback to legacy fields for backward compatibility
        aiMarkingData = {
            finalOverallScore: essayData.finalOverallScore,
            consolidatedMarkingResults: essayData.consolidatedMarkingResults,
            modelName: essayData.modelUsed || 'AI'
        };
    }

    const prompt = `
        You are an expert educational psychologist and AI systems analyst. Your goal is to provide a clear, insightful, and non-judgmental comparison between an AI's automated marking and a human teacher's marking to help the teacher understand the AI's reasoning.

        **ESSAY AND ASSESSMENT DETAILS:**
        - Essay Question: ${assessment.question}
        - Rubric: ${assessment.rubric}
        - Essay Text:
        \`\`\`
        ${essayData.transcribedText}
        \`\`\`

        **DATA FOR COMPARISON:**

        1.  **AI's Final Marking (${aiMarkingData.modelName}):**
            -   Overall Score: ${aiMarkingData.finalOverallScore}
            -   Criteria Breakdown:
                \`\`\`json
                ${JSON.stringify(aiMarkingData.consolidatedMarkingResults, null, 2)}
                \`\`\`

        2.  **Human Teacher's Marking:**
            -   Overall Score: ${essayData.manualOverallScore}
            -   Criteria Breakdown:
                \`\`\`json
                ${JSON.stringify(essayData.manualMarkingResults, null, 2)}
                \`\`\`
        
        ${teacherFeedback ? `
        3.  **Human Teacher's Feedback to Student (for analysis):**
            \`\`\`
            ${teacherFeedback}
            \`\`\`
        ` : ''}

        **YOUR TASK:**
        Generate a detailed analysis in Markdown format explaining the differences between the AI and human teacher's scores. Structure your response into the following sections using the specified headings.

        ### Overall Summary of Marking Variance
        Start with a brief, high-level summary of the comparison. For example, "The ${aiMarkingData.modelName} marker was generally more conservative than the teacher, particularly on the 'Judgement' criterion. The main difference of 5 marks stems from this."

        ### Per-Criterion Analysis
        For each criterion:
        -   If marks are similar, state it briefly. (e.g., "On 'Structure', both ${aiMarkingData.modelName} and teacher agreed the essay was in the top band.")
        -   If marks differ, provide a detailed explanation for the likely reason. Refer to specific rubric descriptors and quote parts of the essay. (e.g., "On 'Historical Knowledge', the teacher awarded the top band (21-25) while the ${aiMarkingData.modelName} awarded the band below (16-20). The AI's own justification suggests it penalized the essay for a minor factual ambiguity regarding the Five-Year Plans. A human teacher likely saw this as insignificant compared to the overall depth of knowledge, whereas the AI, following its rules strictly, may have been unable to overlook it.")

        ${teacherFeedback ? `
        ### Analysis of Teacher's Feedback
        -   **Areas of Agreement:** Point out where the teacher's feedback aligns with the ${aiMarkingData.modelName}'s assessment. (e.g., "Both you and the ${aiMarkingData.modelName} correctly identified the student's strength in using primary sources.")
        -   **Different Perspectives:** Highlight where the teacher's feedback diverges from the ${aiMarkingData.modelName}'s findings and explain the likely reason. (e.g., "While you praised the conclusion, the ${aiMarkingData.modelName} may have found it too brief, lacking a summary of all key points mentioned in the body paragraphs, which is required by the rubric for the top band.")

        ### AI-Powered Recommendations for Your Feedback
        Provide a short, bulleted list of constructive and respectful suggestions for how the teacher could potentially enhance their feedback. Frame this positively.
        -   Example: "To make the feedback even more actionable, you could consider linking your point about 'lacking detail' to a specific sentence in the essay and suggesting what kind of detail could be added."
        -   Example: "Consider adding a point about the essay's strong paragraph transitions, which the ${aiMarkingData.modelName} identified as a key strength in the 'Structure' criterion."
        ` : ''}
    `;

    try {
        const { text } = await callGeminiWithRetry(() =>
            generateTextFromGemini({
                modelId: modelConfig.apiModel,
                parts: [{ text: prompt }],
                generationConfig: {
                    temperature: 0.4, // Moderately low for factual but nuanced language
                    ...modelConfig.config
                },
                safetySettings: SAFETY_SETTINGS
            })
        );
        return text;
    } catch (error) {
        handleGeminiError(error, 'variance explanation');
    }
};

export const transformRubricDocument = async (
    rubricText: string,
    context: RubricTransformContext,
    requestedModelId: string = 'gemini-2.5-flash'
): Promise<RubricTransformResponse> => {
    const cleanedRubric = rubricText?.trim();
    if (!cleanedRubric) {
        throw new Error('No rubric text was provided to transform.');
    }
    if (!context.totalMarks || context.totalMarks <= 0) {
        throw new Error('Total marks must be greater than zero to transform a rubric.');
    }

    const targetModel = MODELS[requestedModelId] || MODELS['gemini-3-pro-preview'];

    const contextLines = [
        `Total marks available: ${context.totalMarks}`,
        context.subject ? `Subject: ${context.subject}` : null,
        context.topic ? `Topic: ${context.topic}` : null,
        context.yearLevel ? `Year level: ${context.yearLevel}` : null,
        context.assessmentType ? `Assessment type: ${context.assessmentType}` : null,
    ].filter(Boolean).join('\n');

    const schema = `{
  "summary": ["Short sentence of how the rubric was interpreted."],
  "warnings": ["Any assumptions or missing details."],
  "criteria": [
    {
      "name": "Criterion name",
      "weightPercent": 40,
      "bands": [
        {
          "bandLabel": "Band 5",
          "minScore": 17,
          "maxScore": 20,
          "descriptor": "Descriptor text that combines the dot points for this band."
        }
      ]
    }
  ]
}`;

    const prompt = `
You are an HSC history assessment specialist. Convert the teacher's existing rubric into a structured JSON format that our AI marking workflow can use.

Context:
${contextLines || 'General humanities task'}

Source Rubric:
<rubric>
${cleanedRubric}
</rubric>

Instructions:
- Identify each distinct marking criterion. Use the teacher's wording exactly where possible.
- Assign weightPercent values that add up to 100. If no weights exist, infer reasonable ones and mention the assumption in warnings.
- Each criterion must include 4 to 6 ordered performance bands with these properties:
    * bandLabel summarises the band name (e.g., "Band 5" or "Outstanding").
    * descriptor merges the teacher's dot points into 2-3 concise sentences.
    * minScore and maxScore are integers and inclusive.
    * The first band's minScore must be 1.
    * The last band's maxScore must equal ${context.totalMarks}.
    * Band score ranges must be contiguous, non-overlapping, and cover all scores from 1-${context.totalMarks}.
- Preserve nuance such as references to historical skills, use of sources, or evaluation depth.
- Keep summary to 1-3 short bullet-style sentences describing what you produced.
- Use warnings to list any assumptions, missing pages, or content you had to infer.

Return ONLY valid JSON matching this schema (no markdown fences or additional commentary):
${schema}
`;

    let rawResponse = '';
    try {
        const { text } = await callGeminiWithRetry(() =>
            generateTextFromGemini({
                modelId: targetModel.apiModel,
                parts: [{ text: prompt }],
                generationConfig: {
                    responseMimeType: "application/json",
                    temperature: 0.2,
                    ...(targetModel.config || {})
                },
                safetySettings: SAFETY_SETTINGS
            })
        );
        rawResponse = text;
        return robustJsonParser(rawResponse);
    } catch (error) {
        handleGeminiError(error, 'rubric transformation', rawResponse);
    }
};
